import User from 'types/User';
import { UnifiedRequest } from 'types/ManageUserMemberships';

export const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

export const matchesSearch = (user: User, searchText: string): boolean => {
  if (!searchText) return true;
  const q = searchText.toLowerCase();
  return (
    user.email.toLowerCase().includes(q) ||
    (user.firstName || '').toLowerCase().includes(q) ||
    (user.lastName || '').toLowerCase().includes(q) ||
    (user.affiliation || '').toLowerCase().includes(q)
  );
};

export const buildPendingRows = (
  pendingNew: User[],
  pendingUpgrade: User[]
): UnifiedRequest[] => [
  ...pendingNew.map(
    (user): UnifiedRequest => ({
      id: `new-${user.email}`,
      requestType: 'new-user',
      name: `${user.firstName} ${user.lastName}`,
      email: user.email,
      affiliation: user.affiliation,
      currentTier: user.membershipTier,
      requestedTier: undefined,
      justification: user.justification,
      requestDate: user.createdAt,
      user,
    })
  ),
  ...pendingUpgrade.map(
    (user): UnifiedRequest => ({
      id: `upgrade-${user.email}`,
      requestType: 'upgrade',
      name: `${user.firstName} ${user.lastName}`,
      email: user.email,
      affiliation: user.affiliation,
      currentTier: user.membershipTier,
      requestedTier: user.requestedTier,
      justification: user.upgradeJustification,
      requestDate: user.upgradeRequestDate,
      user,
    })
  ),
];

export const buildRecentRows = (
  recentNew: User[],
  recentUpgrade: User[]
): UnifiedRequest[] => [
  ...recentNew.map(
    (user): UnifiedRequest => ({
      id: `new-${user.email}`,
      requestType: 'new-user',
      name: `${user.firstName} ${user.lastName}`,
      email: user.email,
      affiliation: user.affiliation,
      currentTier: user.membershipTier,
      justification: user.justification,
      requestDate: user.createdAt,
      decision: user.approved ? 'approved' : 'denied',
      tierGranted: user.membershipTier,
      adminComment: user.adminComment,
      resolvedOn: user.lastUpdated,
      user,
    })
  ),
  ...recentUpgrade.map(
    (user): UnifiedRequest => ({
      id: `upgrade-${user.email}`,
      requestType: 'upgrade',
      name: `${user.firstName} ${user.lastName}`,
      email: user.email,
      affiliation: user.affiliation,
      currentTier: user.membershipTier,
      requestedTier: user.requestedTier,
      justification: user.upgradeJustification,
      requestDate: user.upgradeRequestDate,
      decision: user.upgradeRequestStatus,
      tierGranted: user.approvedTier,
      adminComment: user.adminComment,
      resolvedOn: user.lastUpdated,
      user,
    })
  ),
];

// Helper for checking if user is newly created within past week
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

export const isRecentlyCreated = (createdAt?: string, windowMs = ONE_WEEK_MS): boolean => {
  if (!createdAt) return false;
  const created = new Date(createdAt).getTime();
  if (Number.isNaN(created)) return false;
  return Date.now() - created <= windowMs;
};